import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChallengeDetails,
  Container,
  CountUsers,
  Footer,
  ImageChallenge,
  SeasonDetails,
} from './styles';
import PrimaryButton from '../../Buttons/PrimaryButton';
import {
  RoundCheckIcon,
  SpinnerIcon,
  SubmissionIcon,
  UsersIcon2,
} from '../../../assets/svg/SVGComponents';
import { useModal } from '../../../hooks/modal';
import ChallengeDetailsModal from '../../../modals/ChallengeDetailsModal';
import IChallengeDataProps from '../../../dtos/IChallengeDataProps';

const ChallengeCardHorizontal: React.FC<IChallengeDataProps> = ({
  id,
  slug,
  illustration_url,
  title,
  description,
  start_submission,
  end_submission,
  count_submissions,
  status,
  type,
}) => {
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const formattedSubmissionTime = useMemo(() => {
    const startDate = new Date(start_submission);
    const endDate = new Date(end_submission);

    // Formata as datas para dd/MM
    const formattedStart = startDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });

    const formattedEnd = endDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const daysRemaining = Math.ceil(
      (endDate.getTime() - today.getTime()) / (1000 * 3600 * 24),
    );

    const statusMessage =
      daysRemaining <= 0
        ? 'encerrado'
        : `faltam ${daysRemaining} dia${daysRemaining !== 1 ? 's' : ' '}`;

    return `Período: ${formattedStart} a ${formattedEnd} • ${statusMessage}`;
  }, [end_submission, start_submission]);

  const buttonProps = useMemo(() => {
    if (type === 'submit') {
      switch (status) {
        case 'no_submit':
          return {
            label: 'Participar',
            icon: <SubmissionIcon width="1rem" strokeWidth={2} stroke="#fff" />,
            bgColor: '#FF9800', // Laranja
            disabled: false,
          };
        case 'submited':
          return {
            label: 'Participando',
            icon: <SpinnerIcon width="1rem" strokeWidth={2} stroke="#fff" />,
            bgColor: '#FFA726', // Laranja mais claro
            disabled: false,
          };
        case 'completed':
          return {
            label: 'Concluído',
            icon: <RoundCheckIcon width="1rem" strokeWidth={2} stroke="#fff" />,
            bgColor: '#4CAF50', // Verde
            disabled: false,
          };
        default:
          return {
            label: 'Participar',
            icon: <SubmissionIcon width="1rem" strokeWidth={2} stroke="#fff" />,
            bgColor: '#FF9800',
            disabled: false,
          };
      }
    }

    if (type === 'to-submit') {
      return {
        label: 'Publicando',
        icon: (
          <SpinnerIcon
            width="1rem"
            className="spinner-icon"
            strokeWidth={2}
            stroke="#fff"
          />
        ),
        bgColor: '#FFA726', // Laranja mais claro
        disabled: true,
      };
    }

    return {
      label: '',
      icon: null,
      bgColor: '',
      disabled: true,
    };
  }, [status, type]);

  const handleParticipateClick = useCallback(() => {
    closeModal();
    navigate(`/create/post/challenge/${slug}`);
  }, [closeModal, navigate, slug]);

  return (
    <Container type={type ?? 'submit'}>
      <ImageChallenge src={illustration_url} alt="image-default" />
      <ChallengeDetails>
        <h1>{title}</h1>
        <SeasonDetails>
          <p>{formattedSubmissionTime}</p>
          <span>{description}</span>
        </SeasonDetails>
        <Footer>
          <PrimaryButton
            onClick={() => handleParticipateClick()}
            style={{ backgroundColor: buttonProps.bgColor }}
            disabled={buttonProps.disabled}
          >
            {buttonProps.icon}
            {buttonProps.label}
          </PrimaryButton>
          <CountUsers>
            <UsersIcon2 width="1rem" strokeWidth={2} stroke="#1e88e5" />
            {count_submissions}
          </CountUsers>
        </Footer>
      </ChallengeDetails>
    </Container>
  );
};

export default ChallengeCardHorizontal;
